import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './i18n';
import './estilos.css';
import { App } from './App.tsx';
import { ProveedorAnuncios } from './componentes/Anuncio.tsx';
import { AvisoActualizacion } from './componentes/AvisoActualizacion.tsx';

const cliente = new QueryClient({
  defaultOptions: {
    queries: {
      // En la calle la cobertura va y viene. Se reintenta, pero poco: es mejor
      // enseñar el error y dejar reintentar a mano que dejar la pantalla
      // colgada medio minuto.
      retry: 1,
      refetchOnWindowFocus: true,
      staleTime: 30_000,
    },
    mutations: { retry: 0 },
  },
});

/**
 * En desarrollo NO puede haber ningun service worker, y si lo hay se echa.
 *
 * La configuracion de la PWA no genera service worker en desarrollo
 * (`devOptions.enabled: false`), pero eso solo evita crear uno nuevo: si en
 * algun momento se sirvio una compilacion en este mismo puerto, el navegador se
 * quedo con SU service worker registrado para este origen, y un service worker
 * sobrevive a todo menos a que alguien lo eche.
 *
 * A partir de ahi el servidor de desarrollo queda secuestrado: quien manda es
 * el service worker viejo, que responde con su copia precacheada, asi que se
 * sigue viendo la aplicacion ANTIGUA por mucho que se guarde, se recompile o se
 * recargue. Comprobado el 08/09/2026 reproduciendo el caso: con el servidor de
 * desarrollo en pie, `navigator.serviceWorker.controller` seguia siendo el de
 * la build anterior en tres recargas seguidas.
 *
 * Y no se arregla solo, porque para actualizarse ese service worker pide
 * `/sw.js`, y en desarrollo Vite devuelve el index.html para cualquier ruta que
 * no conoce: el navegador recibe HTML donde espera JavaScript y descarta la
 * actualizacion con `SecurityError: unsupported MIME type ('text/html')`.
 *
 * Esto NO afecta a produccion: `import.meta.env.DEV` es false al compilar, y el
 * bloque entero desaparece del paquete.
 *
 * LO QUE ESTO NO ARREGLA, y conviene saberlo: a quien ya esta secuestrado no lo
 * rescata. El service worker viejo sirve la aplicacion COMPILADA, donde
 * `import.meta.env.DEV` es false, asi que este bloque ni siquiera existe en el
 * codigo que se ejecuta. De ahi no se sale desde dentro: hay que echarlo a mano
 * una vez, desde las herramientas del navegador (Aplicacion, Service Workers,
 * "Unregister") o borrando los datos del sitio. A partir de ahi, esto evita que
 * vuelva a pasar.
 */
if (import.meta.env.DEV && 'serviceWorker' in navigator) {
  void navigator.serviceWorker.getRegistrations().then(async (registros) => {
    if (registros.length === 0) return;
    await Promise.all(registros.map((registro) => registro.unregister()));
    // Las cajas de cache no se van con el registro: sin vaciarlas, lo que sirvio
    // ese service worker se queda ocupando sitio en el disco del navegador.
    if ('caches' in globalThis) {
      const nombres = await caches.keys();
      await Promise.all(nombres.map((nombre) => caches.delete(nombre)));
    }
    console.warn(
      '[recorregut] Habia un service worker registrado en desarrollo y se ha quitado. ' +
        'Recarga la pagina: hasta ahora estabas viendo su copia guardada, no lo que sirve Vite.',
    );
  });
}

const raiz = document.getElementById('raiz');
if (!raiz) throw new Error('Falta el elemento raiz en el documento');

createRoot(raiz).render(
  <StrictMode>
    <QueryClientProvider client={cliente}>
      <BrowserRouter>
        <ProveedorAnuncios>
          <App />
          <AvisoActualizacion />
        </ProveedorAnuncios>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
